import { type FormEvent, useState } from "react";
import { ErrorState, LoadingState } from "@/components/States";
import { Alert, Button, Card, Select, Tag, TextField } from "@/components/ui";
import { errorMessage } from "@/lib/api/client";
import {
  useCreateService,
  useDeletePricing,
  usePricing,
  useRotateKey,
  useServices,
  useSetServiceActive,
  useUpsertPricing,
} from "@/lib/api/queries";
import { formatDateTime } from "@/lib/format";

/** The freshly issued plaintext key — the server will never show it again. */
function IssuedKey({ serviceId, apiKey, onDismiss }: { serviceId: string; apiKey: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Alert
      tone="cautionary"
      title={`${serviceId} 인증키가 발급됐어요`}
      action={
        <Button size="small" variant="text" color="assistive" onClick={onDismiss}>
          닫기
        </Button>
      }
    >
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <code className="select-all break-all rounded-xs bg-background-normal px-2 py-1 text-caption-1 text-label-normal">
          {apiKey}
        </code>
        <Button
          size="tiny"
          variant="outlined"
          color="assistive"
          onClick={() => {
            navigator.clipboard?.writeText(apiKey);
            setCopied(true);
          }}
        >
          {copied ? "복사됨" : "복사"}
        </Button>
      </div>
      <p className="mt-1">지금 저장해 주세요. 이 값은 다시 볼 수 없어요.</p>
    </Alert>
  );
}

function ServicesPanel() {
  const services = useServices();
  const createService = useCreateService();
  const rotateKey = useRotateKey();
  const setActive = useSetServiceActive();

  const [serviceId, setServiceId] = useState("");
  const [name, setName] = useState("");
  const [issued, setIssued] = useState<{ serviceId: string; apiKey: string } | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    createService.mutate(
      { service_id: serviceId, name },
      {
        onSuccess: (data) => {
          setIssued({ serviceId: data.service_id, apiKey: data.api_key });
          setServiceId("");
          setName("");
        },
      }
    );
  }

  return (
    <Card>
      <h2 className="mb-1 text-body-1 font-semibold text-label-strong">서비스 · 인증키</h2>
      <p className="mb-4 text-label-2 text-label-alternative">
        여기서 발급한 키로만 <code className="text-label-normal">POST /v1/events</code> 에 로그를 보낼 수 있어요.
      </p>

      {issued && <IssuedKey {...issued} onDismiss={() => setIssued(null)} />}

      <form onSubmit={submit} className="my-4 flex flex-wrap items-end gap-3">
        <TextField
          label="서비스 ID"
          size="medium"
          placeholder="quote-forge"
          pattern="[a-z0-9][a-z0-9._-]*"
          title="소문자·숫자로 시작하고, 소문자/숫자/. _ - 만 쓸 수 있어요"
          value={serviceId}
          onChange={(event) => setServiceId(event.target.value)}
          required
          style={{ width: 220 }}
        />
        <TextField
          label="표시 이름"
          size="medium"
          placeholder="Quote Forge"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          style={{ width: 220 }}
        />
        <Button type="submit" size="medium" iconLeft="plus" disabled={createService.isPending}>
          등록하고 키 발급
        </Button>
      </form>

      {createService.isError && (
        <Alert tone="negative" title="등록하지 못했어요">
          {errorMessage(createService.error)}
        </Alert>
      )}

      {services.isPending && <LoadingState />}
      {services.isError && <ErrorState error={services.error} onRetry={services.refetch} />}
      {services.data && services.data.length === 0 && (
        <p className="py-6 text-center text-label-2 text-label-alternative">아직 등록된 서비스가 없어요.</p>
      )}
      {services.data && services.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-label-2">
            <thead>
              <tr className="border-b border-line-neutral text-left text-label-alternative">
                <th scope="col" className="px-3 py-2 font-semibold">서비스</th>
                <th scope="col" className="px-3 py-2 font-semibold">상태</th>
                <th scope="col" className="px-3 py-2 font-semibold">등록</th>
                <th scope="col" className="px-3 py-2 font-semibold">마지막 수신</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">작업</th>
              </tr>
            </thead>
            <tbody>
              {services.data.map((service) => (
                <tr key={service.service_id} className="border-b border-line-alternative last:border-0">
                  <th scope="row" className="px-3 py-2 text-left font-medium text-label-normal">
                    {service.name}
                    <span className="ml-2 text-caption-1 text-label-assistive">{service.service_id}</span>
                  </th>
                  <td className="px-3 py-2">
                    <Tag size="small" color={service.is_active ? "positive" : "neutral"}>
                      {service.is_active ? "활성" : "회수됨"}
                    </Tag>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-label-alternative">
                    {formatDateTime(service.created_at)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-label-alternative">
                    {service.last_seen_at ? formatDateTime(service.last_seen_at) : "-"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        size="tiny"
                        variant="outlined"
                        color="assistive"
                        onClick={() =>
                          rotateKey.mutate(service.service_id, {
                            onSuccess: (data) => setIssued({ serviceId: data.service_id, apiKey: data.api_key }),
                          })
                        }
                      >
                        키 재발급
                      </Button>
                      <Button
                        size="tiny"
                        variant="text"
                        color="assistive"
                        onClick={() =>
                          setActive.mutate({ serviceId: service.service_id, active: !service.is_active })
                        }
                      >
                        {service.is_active ? "회수" : "재활성"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const PROVIDERS = ["openai", "anthropic", "google", "azure", "cohere", "other"];

function PricingPanel() {
  const pricing = usePricing();
  const upsert = useUpsertPricing();
  const remove = useDeletePricing();

  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("");
  const [inputPrice, setInputPrice] = useState("");
  const [outputPrice, setOutputPrice] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));

  function submit(event: FormEvent) {
    event.preventDefault();
    upsert.mutate(
      {
        provider,
        model,
        input_price_per_1k: inputPrice,
        output_price_per_1k: outputPrice,
        // Date input is local-midnight; the API stores UTC.
        effective_from: new Date(`${effectiveFrom}T00:00:00`).toISOString(),
      },
      { onSuccess: () => setModel("") }
    );
  }

  return (
    <Card>
      <h2 className="mb-1 text-body-1 font-semibold text-label-strong">모델 가격표</h2>
      <p className="mb-4 text-label-2 text-label-alternative">
        클라이언트가 <code className="text-label-normal">cost_usd</code> 를 안 보내면, 이벤트 발생 시각에 유효한
        가격으로 서버가 계산해요. 이미 저장된 비용은 다시 계산하지 않아요.
      </p>

      <form onSubmit={submit} className="mb-4 flex flex-wrap items-end gap-3">
        <Select
          label="프로바이더"
          size="medium"
          value={provider}
          options={PROVIDERS}
          onChange={(event) => setProvider(event.target.value)}
          style={{ width: 160 }}
        />
        <TextField
          label="모델"
          size="medium"
          placeholder="gpt-4o"
          value={model}
          onChange={(event) => setModel(event.target.value)}
          required
          style={{ width: 200 }}
        />
        <TextField
          label="입력 $/1k"
          size="medium"
          type="number"
          step="0.000001"
          min="0"
          value={inputPrice}
          onChange={(event) => setInputPrice(event.target.value)}
          required
          style={{ width: 140 }}
        />
        <TextField
          label="출력 $/1k"
          size="medium"
          type="number"
          step="0.000001"
          min="0"
          value={outputPrice}
          onChange={(event) => setOutputPrice(event.target.value)}
          required
          style={{ width: 140 }}
        />
        <TextField
          label="적용 시작"
          size="medium"
          type="date"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
          required
          style={{ width: 170 }}
        />
        <Button type="submit" size="medium" iconLeft="plus" disabled={upsert.isPending}>
          저장
        </Button>
      </form>

      {upsert.isError && (
        <Alert tone="negative" title="저장하지 못했어요">
          {errorMessage(upsert.error)}
        </Alert>
      )}

      {pricing.isPending && <LoadingState />}
      {pricing.isError && <ErrorState error={pricing.error} onRetry={pricing.refetch} />}
      {pricing.data && pricing.data.length === 0 && (
        <p className="py-6 text-center text-label-2 text-label-alternative">
          가격표가 비어 있어요. 비용이 전부 $0 으로 잡혀요.
        </p>
      )}
      {pricing.data && pricing.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-label-2">
            <thead>
              <tr className="border-b border-line-neutral text-left text-label-alternative">
                <th scope="col" className="px-3 py-2 font-semibold">프로바이더</th>
                <th scope="col" className="px-3 py-2 font-semibold">모델</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">입력 $/1k</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">출력 $/1k</th>
                <th scope="col" className="px-3 py-2 font-semibold">적용 시작</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">작업</th>
              </tr>
            </thead>
            <tbody>
              {pricing.data.map((row) => (
                <tr key={row.id} className="border-b border-line-alternative last:border-0">
                  <td className="px-3 py-2 text-label-normal">{row.provider}</td>
                  <th scope="row" className="px-3 py-2 text-left font-medium text-label-normal">{row.model}</th>
                  <td className="px-3 py-2 text-right tabular-nums">{row.input_price_per_1k}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.output_price_per_1k}</td>
                  <td className="px-3 py-2 tabular-nums text-label-alternative">
                    {formatDateTime(row.effective_from)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="tiny" variant="text" color="assistive" onClick={() => remove.mutate(row.id)}>
                      삭제
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function Admin() {
  return (
    <div className="flex flex-col gap-4">
      <ServicesPanel />
      <PricingPanel />
    </div>
  );
}
